import {
  Box,
  Button,
  Heading,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Stack,
  useDisclosure,
} from '@chakra-ui/react';
import {
  createPrismaQuery,
  createPrismaSort,
  DataTable,
  DataTableAddFilter,
  DataTableFilter,
  DataTableFilters,
  DataTableSearch,
  useCrud,
} from '@pelicargo/datatable';
import { AircraftType } from '@pelicargo/types';
import { Show, useError, useSuccess } from '@pelicargo/ui';
import { aircraftTypeOptions, getPrettyAircraftType, instructionUsageOptions } from '@pelicargo/utils';
import { createColumnHelper } from '@tanstack/react-table';
import { useCallback, useMemo, useState } from 'react';
import { HiEllipsisVertical } from 'react-icons/hi2';

import { AlertModal } from '../components/AlertModal/AlertModal';
import { InstructionFormDrawer } from '../components/Drawers/InstructionFormDrawer';
import { trpc } from '../config/trpc';

const instructionFilters = [
  {
    name: 'aircraft_type',
    label: 'Aircraft Type',
    type: 'string',
    inputType: 'checkbox',
    operator: 'in',
    value: [],
    options: aircraftTypeOptions,
  },
  {
    name: 'provider_id',
    label: 'Provider ID',
    type: 'string',
    inputType: 'string',
    operator: 'contains',
    mode: 'insensitive',
    value: '',
  },
  {
    name: 'airport_id',
    label: 'Airport ID',
    type: 'string',
    inputType: 'string',
    operator: 'contains',
    mode: 'insensitive',
    value: '',
  },
  {
    name: 'usage',
    label: 'Instruction Usage',
    type: 'string',
    inputType: 'checkbox',
    operator: 'in',
    value: [],
    options: instructionUsageOptions,
  },
] satisfies DataTableFilter[];

type TableColumns = {
  id: number;
  aircraft_type: string;
  booking_instructions: string;
  dropoff_instructions: string;
  provider_id: string;
  airport_id: string;
  action?: any;
};

export const InstructionsScreen = () => {
  const instructionDrawer = useDisclosure();
  const deleteModal = useDisclosure();
  const { handleSuccess } = useSuccess();
  const { handleError } = useError();

  const { query, handleQueryChange, filters, setFilters, sorting, setSorting, pagination, setPagination, take, skip } =
    useCrud();

  const [selectedInstructionId, setSelectedInstructionId] = useState<number | null>(null);

  const { data, refetch } = trpc.searchInstructions.useQuery({
    skip,
    take,
    search: query,
    orderBy: createPrismaSort(sorting),
    filters: createPrismaQuery(filters),
  });
  const count = data?.[0];

  const { mutateAsync: deleteInstruction, isLoading: isDeleting } = trpc.deleteInstruction.useMutation();

  const handleAddFilter = (filter: DataTableFilter) => () => {
    setFilters((prevFilters) => [...prevFilters, filter]);
  };

  const handleClear = () => {
    setFilters([]);
  };

  const handleShowDeleteModal = useCallback(
    (id: number) => {
      setSelectedInstructionId(id);
      deleteModal.onOpen();
    },
    [deleteModal],
  );

  const handleDelete = useCallback(async () => {
    try {
      await deleteInstruction({ id: selectedInstructionId as number });
      refetch();
      setSelectedInstructionId(null);
      handleSuccess('Instruction deleted');
      deleteModal.onClose();
    } catch (error) {
      handleError(error);
    }
  }, [deleteInstruction, deleteModal, handleError, handleSuccess, refetch, selectedInstructionId]);

  const onRowClick = useCallback(
    (row: TableColumns) => {
      setSelectedInstructionId(row?.id);
      instructionDrawer.onOpen();
    },
    [instructionDrawer],
  );

  const handleClose = () => {
    setSelectedInstructionId(null);
    refetch();
    instructionDrawer.onClose();
  };

  const columnHelper = createColumnHelper<TableColumns>();
  const columns = useMemo(
    () => [
      columnHelper.accessor('id', {
        header: 'ID',
        minSize: 0,
        size: 0,
      }),
      columnHelper.accessor('provider_id', {
        header: 'Airline',
      }),
      columnHelper.accessor('airport_id', {
        header: 'Airport',
      }),
      columnHelper.accessor('aircraft_type', {
        header: 'Aircraft Type',
        cell: (cell) => getPrettyAircraftType(cell.getValue() as AircraftType),
      }),
      columnHelper.accessor('action', {
        header: '',
        size: 0,
        minSize: 0,
        cell: (cell) => {
          const row = cell.row.original;
          return (
            <Box onClick={(e) => e.stopPropagation()}>
              <Menu size="xs">
                <MenuButton
                  as={IconButton}
                  aria-label="Options"
                  size="sm"
                  icon={<HiEllipsisVertical />}
                  variant="ghost"
                />
                <MenuList>
                  <MenuItem onClick={() => onRowClick(row)}>Update</MenuItem>
                  <MenuItem onClick={() => handleShowDeleteModal(row?.id)}>Delete</MenuItem>
                </MenuList>
              </Menu>
            </Box>
          );
        },
      }),
    ],
    [columnHelper, handleShowDeleteModal, onRowClick],
  );

  return (
    <Stack spacing="0" h="full">
      <Stack p="4" spacing="4">
        <Stack direction="row" justify="space-between" zIndex="99" w="full">
          <Stack direction="row" align="center" w="full">
            <Heading variant="h3">Instructions</Heading>
            <DataTableSearch value={query} onChange={handleQueryChange} />
            <DataTableAddFilter
              activeFilters={filters?.map((filter) => filter.name)}
              options={instructionFilters}
              handleAddFilter={handleAddFilter}
            />
          </Stack>
          <Button colorScheme="brand" onClick={instructionDrawer.onOpen}>
            Create Instruction
          </Button>
        </Stack>
        <Show if={filters.length > 0}>
          <DataTableFilters filters={filters} setFilters={setFilters} onClear={handleClear} />
        </Show>
      </Stack>
      <DataTable
        columns={columns}
        data={data?.[1] ?? []}
        dataCount={count ?? 0}
        pagination={pagination}
        setPagination={setPagination}
        sorting={sorting}
        setSorting={setSorting}
        onRowClick={onRowClick}
      />
      <InstructionFormDrawer
        isOpen={instructionDrawer.isOpen}
        onClose={handleClose}
        instructionId={selectedInstructionId}
      />
      <AlertModal
        isOpen={deleteModal.isOpen}
        onClose={deleteModal.onClose}
        title="Delete Instruction"
        message="Are you sure?"
        btnCancel="Cancel"
        btnDelete="Delete"
        handleConfirm={handleDelete}
        isDeleting={isDeleting}
      />
    </Stack>
  );
};
